import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames/bind'
import { useTranslation } from 'react-i18next'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { Box, CircularProgress, IconButton } from '@mui/material'
import { XMarkIcon } from '~/assets/icons'
import { getListRecommendMenuApi } from '~/apis/recommendMenu'
import { selectRestaurant } from '~/redux/restaurantSlice'
import { useSelector } from 'react-redux'
import RecommendItem from './RecommendItem'
import { useNavigate } from 'react-router-dom'
import { path } from '~/routes/path'

import styles from './index.module.scss'
import { useThemeContext } from '~/theme/ThemeProvider'
import { checkFoodRecommendInCart, dataCartSelector } from '~/redux/cartSlice'
const cx = classNames.bind(styles)

const PopupRecommendMenu = ({ setOpen }) => {
  const navigate = useNavigate()
  const { restaurantId } = useSelector(selectRestaurant)
  const dataCart = useSelector(dataCartSelector)
  const [recommendItems, setRecommendItems] = useState([])
  const [loading, setLoading] = useState(false)
  const { currentThemeObject } = useThemeContext()
  const { color20, colorText10, colorText11 } = currentThemeObject
  const colorDisabled = 'rgba(0, 0, 0, 0.25)'
  const checkDisabledButton = checkFoodRecommendInCart(dataCart, recommendItems)

  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language

  const getListRecomendMenus = useCallback(async () => {
    if (restaurantId) {
      setLoading(true)
      const { data, isSuccess } = await getListRecommendMenuApi(restaurantId)
      if (isSuccess) {
        setRecommendItems(data)
      }
      setLoading(false)
    }
  }, [restaurantId])

  useEffect(() => {
    getListRecomendMenus()
  }, [getListRecomendMenus, currentLanguage])

  const handleClose = () => {
    setOpen(false)
  }

  // Hiển thị phần đầu
  const renderHeader = () => {
    return (
      <Box component="h1" className={cx('title')}>
        {t('titles.popular_add_ons')}
      </Box>
    )
  }

  const renderCloseBtn = () => {
    return (
      <IconButton className={cx('close_btn')} onClick={handleClose}>
        <XMarkIcon color="#000" size="16" />
      </IconButton>
    )
  }

  // Hiển thị phần content main list
  const renderContent = () => {
    if (loading) {
      return (
        <Box component="div" className={cx('loading')}>
          <CircularProgress />
        </Box>
      )
    }

    return (
      <Box component="div" className={cx('recommend_items')}>
        {recommendItems.map((item) => {
          return (
            <div key={item.id} className={cx('recommend_item')}>
              <RecommendItem {...item} />
            </div>
          )
        })}
      </Box>
    )
  }

  // Xử lý di chuyển đến màn hình ordering
  const handleConfirm = () => {
    setOpen(false)
    navigate(path.ordering)
  }

  // Hiển thị nút confirm
  const renderConfirmBtn = () => {
    const bgColor = !checkDisabledButton ? colorDisabled : color20
    const color = !checkDisabledButton ? colorText10 : colorText11

    return (
      <Button
        style={{
          backgroundColor: bgColor,
          color,
        }}
        onClick={handleConfirm}
        className={cx('confirm_btn', { disabled: !checkDisabledButton })}>
        {t('actions.confirm')}
      </Button>
    )
  }

  // Nếu trong trường hợp không có data -> sẽ không hiển thị luôn
  if (recommendItems.length <= 0) return null

  return (
    <React.Fragment>
      <Dialog
        open={true}
        onClose={handleClose}
        scroll="paper"
        aria-labelledby="scroll-dialog-title"
        aria-describedby="scroll-dialog-description"
        classes={{
          paper: cx('paper'),
        }}>
        {renderCloseBtn()}
        <DialogTitle id="scroll-dialog-title" component="div" className={cx('header_wrap')}>
          {renderHeader()}
        </DialogTitle>
        <DialogContent className={cx('body_wrap')}>{renderContent()}</DialogContent>
        <DialogActions className={cx('footer_wrap')}>{renderConfirmBtn()}</DialogActions>
      </Dialog>
    </React.Fragment>
  )
}

PopupRecommendMenu.propTypes = {
  setOpen: PropTypes.func,
}

export default React.memo(PopupRecommendMenu)
